import { ApiError } from '@/api/client'

// The one frontend place that turns a thrown value into what the operator
// sees (item 4.6). It never maps error codes to its own text: every envelope
// message already comes from the backend's single code-to-message table
// (gui_backend/core/errors.py::_CLASS_MESSAGES), so a second table here
// could only drift from it. Only `message` and `detail.errors[]` are ever
// read - never `stack`, `request_id` or the rest of `detail`.

// One field-level issue from a 4xx envelope's `detail.errors[]` (schema,
// lookup-collision or publish validation) - `path` is the dotted config path.
export interface FieldError {
  path: string
  message: string
}

// Any thrown value's message, or undefined so the caller supplies its own
// fallback (`errorMessage(error) ?? 'Failed to ...'`). A network failure is a
// plain Error, so its browser text ("Failed to fetch") is shown as-is.
export function errorMessage(error: unknown): string | undefined {
  return error instanceof Error ? error.message : undefined
}

// Backend responses only - a network failure gives undefined, so the
// Publish/Go live modal titles keep their own fallback text instead.
export function apiErrorMessage(error: unknown): string | undefined {
  return error instanceof ApiError ? error.message : undefined
}

// The backend keeps `detail` on a 4xx and strips it on a 5xx
// (gui_backend/core/errors.py::_sanitize_detail), so this is only ever set
// for a 4xx; undefined otherwise (generic banner, no field highlight).
export function extractFieldErrors(error: unknown): FieldError[] | undefined {
  if (!(error instanceof ApiError)) return undefined
  const detail = error.detail
  if (
    typeof detail === 'object' &&
    detail !== null &&
    'errors' in detail &&
    Array.isArray((detail as { errors: unknown }).errors)
  ) {
    return (detail as { errors: FieldError[] }).errors
  }
  return undefined
}

// "Save failed" banner body: every field error as "path: message", joined
// by "; ", else the plain message. A screen that already shows some field
// errors inline passes only the rest (ClientDetailScreen).
export function formatSaveError(error: unknown, fieldErrors = extractFieldErrors(error)): string | undefined {
  return fieldErrors && fieldErrors.length > 0
    ? fieldErrors.map((err) => `${err.path}: ${err.message}`).join('; ')
    : errorMessage(error)
}
